import React, { useEffect, useRef, useState } from 'react';
import { IconCaretDownWhite } from '../../assets/icons';
import { getBedIconByKey } from './BedRoomHelper';
import { TranslationUtils } from '../../utils';
import { BedRoomSizeName } from '../../types/bedRoom.type';
import _ from 'lodash';

export interface BedRoomBoxProps {
    title: string;
    sleep: number;
    beds: BedType[];
    boxRef: React.RefObject<HTMLDivElement>;
}

export interface BedType {
    title: string;
    icon_key: string;
}

export const BedRoomBox: React.FC<BedRoomBoxProps> = (props: BedRoomBoxProps) => {
    const { title, sleep, beds: bedsProps, boxRef } = props;
    const beds = _.isNil(bedsProps) ? [] : bedsProps;
    const bedInfoRef = useRef<HTMLDivElement>(null);
    const bedBtnRef = useRef<HTMLButtonElement>(null);
    const [showAllBeds, setShowAllBeds] = useState<boolean>(false);
    const [iconRotation, setIconRotation] = useState<number>(0);
    const [bedBoxClass, setBedBoxClass] = useState<string>('bed-room-box-three-or-less');
    const [numberBedForSow, setNumberBedForShow] = useState<number>(3);
    const [displayedBeds, setDisplayedBeds] = useState<Array<BedType>>(beds.slice(0, numberBedForSow));
    const [, setMaxHeight] = useState<number>(194);
    const showBtn = beds.length > numberBedForSow;

    const getBedRoomContainerHeight = (): number => {
        const bedInfoContainer = bedInfoRef.current!;
        const btn = bedBtnRef.current!;

        const bedBoxTopMargin = window.getComputedStyle(bedInfoContainer).marginTop;
        const bexBoxBottomMargin = window.getComputedStyle(bedInfoContainer).marginBottom;
        const bedBoxTotalMargin = parseFloat(bedBoxTopMargin) + parseFloat(bexBoxBottomMargin);
        return bedBoxTotalMargin + bedInfoContainer.clientHeight + btn.clientHeight;
    };

    useEffect(() => {
        if (_.isNil(boxRef) || _.isNil(boxRef.current)) return;
        if (_.isNil(bedInfoRef) || _.isNil(bedInfoRef.current)) return;
        if (_.isNil(bedBtnRef) || _.isNil(bedBtnRef.current)) return;

        const bedBoxContainer = boxRef.current;
        const totalBedBoxHeight = getBedRoomContainerHeight();

        if (totalBedBoxHeight >= bedBoxContainer.clientHeight) {
            setNumberBedForShow(2);
            setDisplayedBeds(beds.slice(0, 2));
        }
    }, [boxRef]);

    useEffect(() => {
        if (_.isNil(boxRef) || _.isNil(boxRef.current)) return;
        if (_.isNil(bedInfoRef) || _.isNil(bedInfoRef.current)) return;
        if (_.isNil(bedBtnRef) || _.isNil(bedBtnRef.current)) return;

        setMaxHeight((prev) => {
            const totalBedBoxHeight = getBedRoomContainerHeight();
            if (totalBedBoxHeight > prev) {
                boxRef.current!.style.maxHeight = `${totalBedBoxHeight}px`;
            }
            return totalBedBoxHeight;
        });
    }, [displayedBeds]);

    const toggleShowAllBeds = () => {
        if (showAllBeds) {
            setDisplayedBeds(beds.slice(0, numberBedForSow));
            setIconRotation(0);
            setBedBoxClass('bed-room-box-three-or-less');
        } else {
            setDisplayedBeds(beds);
            setIconRotation(180);
            setBedBoxClass('bed-room-box-more-than-three');
        }
        setShowAllBeds(!showAllBeds);
    };

    return (
        <div
            ref={boxRef}
            id={'bed-room-box'}
            className={`bed-room-box d-flex flex-column justify-content-between ${bedBoxClass}`}
            data-testid={'bed-room-box'}
        >
            <div ref={bedInfoRef} id={'bed-info'} className={`bed-info mt-3 ${showAllBeds ? 'mb-3' : ''}`}>
                <div id={'bed-info-header'}>
                    <h5 id={'title'} className={'title'}>
                        {title}
                    </h5>
                    <p id={'subtitle'} className={'subtitle mt-0'}>{`${TranslationUtils.get('sleeps').toLowerCase()} ${sleep}`}</p>
                </div>
                {displayedBeds.map((bed, index) => (
                    <div key={index} id={`bed-info-box-${index}`} className={'d-flex mb-1'}>
                        <span id={'icon'} className={'mr-2'}>
                            {getBedIconByKey(bed.icon_key as BedRoomSizeName)}
                        </span>
                        <p id={'description'} className={'description m-0'}>
                            {bed.title}
                        </p>
                    </div>
                ))}
            </div>
            <button
                ref={bedBtnRef}
                id={'bed-btn'}
                className={`bed-btn d-flex align-items-center justify-content-start w-100 ${showBtn ? '' : 'btn-hidden'}`}
                onClick={toggleShowAllBeds}
            >
                <div id={'bed-btn-content'} className={'btn-text d-flex align-items-center ml-4'}>
                    {showAllBeds ? TranslationUtils.get('view_less') : TranslationUtils.get('view_more')}
                    <span
                        id={'bed-btn-icon'}
                        className={'btn-arrow align-middle ml-1'}
                        style={{
                            transform: `rotate(${iconRotation}deg)`,
                        }}
                    >
                        <IconCaretDownWhite />
                    </span>
                </div>
            </button>
        </div>
    );
};
